import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Factory } from '../../production-inventory.model';
import { CompanyFactoryService } from '../../services/company-factory.service';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';

@Component({
  selector: 'app-factories',
  standalone: false,
  templateUrl: './factories.component.html',
  styleUrl: './factories.component.scss',
})
export class FactoriesComponent implements OnInit, OnDestroy {
  @ViewChild('factoryModal') factoryModalRef!: TemplateRef<any>;

  protected factories: Factory[] = [];
  protected isDataLoading = false;
  protected isSaving = false;
  protected isEditMode = false;
  protected modalRef: NgbModalRef | null = null;
  protected factoryForm!: FormGroup;
  protected formError: string | null = null;

  private editingFactoryId: string | null = null;

  constructor(
    private companyFactoryService: CompanyFactoryService,
    private modalService: NgbModal,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadFactories();
  }

  private initForm(): void {
    this.factoryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      address: [''],
    });
  }

  private loadFactories(): void {
    this.isDataLoading = true;
    this.companyFactoryService
      .listFactories()
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (factories: Factory[]) => {
          this.factories = factories;
        },
      });
  }

  protected onAddFactory(): void {
    this.isEditMode = false;
    this.editingFactoryId = null;
    this.formError = null;
    this.factoryForm.reset({ name: '', address: '' });

    const options: NgbModalOptions = { backdrop: 'static', size: 'md' };
    this.modalRef = this.modalService.open(this.factoryModalRef, options);
  }

  protected onEditFactory(factory: Factory): void {
    this.isEditMode = true;
    this.editingFactoryId = factory.id;
    this.formError = null;

    this.factoryForm.patchValue({
      name: factory.name,
      address: factory.address,
    });

    const options: NgbModalOptions = { backdrop: 'static', size: 'md' };
    this.modalRef = this.modalService.open(this.factoryModalRef, options);
  }

  protected onDeleteFactory(factory: Factory): void {
    const ref = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.title = 'Delete Factory';
    ref.componentInstance.message = `Are you sure you want to delete "${factory.name}"?`;
    ref.componentInstance.warning = 'This action cannot be undone.';

    ref.result.then(() => {
      this.companyFactoryService.deleteFactory(factory.id).subscribe({
        next: () => {
          this.factories = this.factories.filter(f => f.id !== factory.id);
        },
      });
    });
  }

  protected onSubmit(): void {
    if (this.factoryForm.invalid) return;

    this.isSaving = true;
    this.formError = null;

    const payload = {
      name: this.factoryForm.get('name')!.value.trim(),
      address: this.factoryForm.get('address')!.value?.trim() || '',
    };

    if (this.isEditMode && this.editingFactoryId) {
      this.companyFactoryService
        .updateFactory(this.editingFactoryId, payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (updated: Factory) => {
            const idx = this.factories.findIndex(f => f.id === updated.id);
            if (idx !== -1) this.factories[idx] = updated;
            this.modalRef?.dismiss();
          },
          error: () => {
            this.formError = 'Failed to update factory. Please try again.';
          },
        });
    } else {
      this.companyFactoryService
        .createFactory(payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (created: Factory) => {
            this.factories = [...this.factories, created];
            this.modalRef?.dismiss();
          },
          error: () => {
            this.formError = 'Failed to create factory. Please try again.';
          },
        });
    }
  }

  protected isInvalid(field: string): boolean {
    const ctrl = this.factoryForm.get(field);
    return (ctrl?.invalid && (ctrl.touched || ctrl.dirty)) ?? false;
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
  }
}
